import {
  ClipboardDocumentListIcon,
  LinkIcon
} from '@heroicons/react/24/outline'
import { PaperClipIcon } from '@heroicons/react/24/solid'
import PropTypes from 'prop-types'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { QUERY_KEYS } from 'constants/constants'
import { ActivityIndicator as ActivityIndicatorAivy } from '../'
import { REQUIREMENTPROFILE } from '../../constants/evaluation-types'
import trackEvents from '../../constants/track-events'
import {
  useAivyContext,
  useLanguageContext,
  useNotificationContext,
  usePartnerContext,
  useUserContext
} from '../../context'
import { query } from '../../graphql'
import {
  BUILD_ENV,
  RESTIFY_DEV_URL,
  RESTIFY_PROD_URL,
  RESTIFY_STAGING_URL,
  convertNumberToTime,
  toBase64
} from '../../helpers'
import { trackEvent } from '../../helpers/analytics'
import { openPdfLoadingWindow } from '../../helpers/pdf-loading'
import { useCopyToClipboard } from '../../hooks/use-copy-to-clipboard'
import { usePartnerIntegrations } from '../../hooks/use-partner-integrations'
import { useCareerPresetQuery } from '../../hooks/useCareerPresetQuery'
import ChallengeMaterialCard from '../challenge-material-card'
import Feedback from '../feedback'
import ProfileFeedbackModal from '../feedback/profile-feedback-modal'
import { AnimatedCopyIcon } from '../motion'
import Panel from '../tailwind/Panel'
import SimpleStats from '../tailwind/simple-stats'

const I18N_PREFIX = 'pages.career.'

const getDvinciUrl = ({ career_id }) => {
  const path = `/ats/dvinci/invite?testId=${career_id}`

  switch (BUILD_ENV) {
    case 'develop':
      return RESTIFY_DEV_URL + path
    case 'staging':
      return RESTIFY_STAGING_URL + path
    default:
      return RESTIFY_PROD_URL + path
  }
}

const Overview = ({ career }) => {
  const { t } = useTranslation()

  const { partner } = usePartnerContext()
  const { system } = useAivyContext()
  const { language } = useLanguageContext()
  const { success, error } = useNotificationContext()
  const {
    cognitoUser: { username },
    activeSpace: { hasSeenOrSubmittedRequirementProfileFeedback }
  } = useUserContext()

  const navigate = useNavigate()

  const dvinciUrlCopy = useCopyToClipboard({ resetAfter: 1000 * 3 })
  const [displayFeedbackModal, setDisplayFeedbackModal] = useState(false)

  const { status: partnerIntegrationsStatus, data: dvinciIntegration } =
    usePartnerIntegrations({
      select: (response) => {
        return response.find(
          ({ remote_service }) => remote_service === 'DVINCI'
        )
      }
    })

  const { id: career_id, diagnostic_version } = career
  const { career_preset_id } = JSON.parse(career.career_analyse)

  const { data: preset, status: presetStatus } =
    useCareerPresetQuery(career_preset_id)

  const { data: careerAnalyses, status: careerAnalysesStatus } = useQuery({
    queryKey: QUERY_KEYS.analysesByCareer(career_id),
    queryFn: () =>
      query({
        query: 'careerAnalyseByCareer',
        variables: { career_id }
      }),
    select: (data) =>
      (data || []).map((analyse) => ({
        ...analyse,
        preset: analyse.preset ? JSON.parse(analyse.preset) : null
      })),
    retry: 1,
    staleTime: 5 * 60 * 1000 // 5 Minuten
  })

  const displayProfileFeedback =
    diagnostic_version === 2 && !hasSeenOrSubmittedRequirementProfileFeedback

  const generatePdf = async () => {
    if (displayProfileFeedback) setDisplayFeedbackModal(true)

    const pdfDocument = openPdfLoadingWindow({ t, language })

    const variables = {
      career_id,
      language,
      type: diagnostic_version === 2 ? REQUIREMENTPROFILE : undefined
    }

    try {
      const pdf = await query({ query: 'generatePdf', variables })
      pdfDocument.location.href = JSON.parse(pdf).headers.Location

      success(t('pdf_success'))
      trackEvent(trackEvents.VIEW_REQUIREMENT_PROFILE, { diagnostic_version })
    } catch (err) {
      error()
      console.error(err)
      pdfDocument.close()
    }
  }

  const queryClient = useQueryClient()

  const spaces_to_careers = useMemo(() => {
    const { state } =
      queryClient.getQueryCache().find({
        queryKey: QUERY_KEYS.talentsByPartner(partner.id, username)
      }) || {}

    if (state?.status !== 'success') return null

    const result = new Map()

    // queryKey: ['talents', partner.id, username]
    // queryFn: () => Promise.all([getAllSpaces(), getCareers()])
    // select: [0] = spaces / [1] = careers
    state.data[0].forEach((space) =>
      result.set(
        space.career_id,
        (result.get(space.career_id) || []).concat([{ ...space }])
      )
    )

    // make sure the selected career has an entry
    if (!result.get(career.id)) {
      result.set(career.id, [])
    }

    return result
  }, [queryClient, career.id, partner.id, username])

  // Unbekannte exam_ids ueberspringen statt crashen: system.challenges kommt
  // aus dem Backend-System-Item CHALLENGES. Fehlt dort ein Eintrag (z.B.
  // CULTURE in einer nicht geseedeten Stage), riss der ungepruefte Zugriff
  // ueber die globale ErrorBoundary das ganze Dashboard mit.
  const challenges = JSON.parse(career.app_settings?.challenges || '[]')
    .map(({ exam_id }) => system.challenges[exam_id])
    .filter(Boolean)

  const timeToComplete = challenges
    .map(({ timeToComplete }) => timeToComplete)
    .reduce((previousValue, currentValue) => previousValue + currentValue, 0)

  const navigateToTalents = () => {
    const location = {
      pathname: '/talents',
      search: `computed=${toBase64([{ assessment_id: { in: [career_id] } }])}`
    }

    localStorage.setItem(location.pathname, location.search)
    navigate(location.pathname)
  }

  const number_of_invited_talents = spaces_to_careers?.get(career_id).length

  const number_of_completed_talents = spaces_to_careers
    ?.get(career_id)
    .filter(({ progress }) => progress === 100).length

  const is_loading =
    (career_preset_id && presetStatus === 'pending') ||
    partnerIntegrationsStatus === 'pending' ||
    careerAnalysesStatus === 'pending'

  if (is_loading) {
    return (
      <div className='mx-8 my-8'>
        <ActivityIndicatorAivy />
      </div>
    )
  }

  const dvinciUrl = dvinciIntegration && getDvinciUrl({ career_id })

  const copyDvinciUrlToClipboard = async () => {
    if (!(await dvinciUrlCopy.copy(dvinciUrl))) {
      error(t('global.copy_error'))
      return
    }

    success(t('career.overview.dvinci_copy_success'))
  }

  return (
    <>
      <div className='mb-12 mt-4'>
        <div className='my-6 md:flex md:items-center'>
          <div className='md:flex-auto'>
            <h1 className='text-xl font-semibold text-gray-900'>
              {t('career.overview.title')}
            </h1>
            <p className='mt-2 max-w-lg text-sm text-gray-700 lg:max-w-2xl'>
              {t('career.overview.description', {
                career_title: career.title
              })}
            </p>
          </div>
        </div>
        <Panel className='mt-4'>
          <SimpleStats
            stats={[
              {
                name: t('career.overview.stats_name_number_of_invited_talents'),
                value: number_of_invited_talents ?? 'n/a',
                onClick: number_of_invited_talents
                  ? navigateToTalents
                  : undefined
              },
              {
                name: t(
                  'career.overview.stats_name_number_of_completed_talents'
                ),
                value: number_of_completed_talents ?? 'n/a'
              },
              {
                name: t('career.overview.stats_name_number_of_challenges'),
                value: challenges.length || 'n/a'
              },
              {
                name: t('career.overview.stats_name_time_to_complete'),
                value:
                  convertNumberToTime(timeToComplete / 60000).minutes || 'n/a',
                unit: t('career.overview.stats_unit_time_to_complete')
              }
            ]}
            background='bg-gray-50'
          />
        </Panel>
        <Panel className='mt-4'>
          <h3 className='text-lg font-medium leading-6 text-gray-900'>
            {t('career.overview.requirement_profile_section_title')}
          </h3>
          <p className='mt-1 max-w-lg text-sm text-gray-700 lg:max-w-2xl'>
            {career_preset_id
              ? t(
                  'career.overview.requirement_profile_section_description_preset',
                  { preset_name: preset.name[language] }
                )
              : t('career.overview.requirement_profile_section_description')}
          </p>
          <div className='mt-4 max-w-2xl'>
            <ul
              role='list'
              className='divide-y divide-gray-100 rounded-md border border-gray-200 bg-white'
            >
              <li
                onClick={generatePdf}
                className='group flex cursor-pointer items-center justify-between py-4 pl-4 pr-5 text-sm leading-6 hover:bg-gray-50/50'
              >
                <div className='flex w-0 flex-1 items-center'>
                  <PaperClipIcon
                    className='h-5 w-5 flex-shrink-0 text-gray-400'
                    aria-hidden='true'
                  />
                  <div className='ml-4 flex min-w-0 flex-1 gap-2'>
                    <span className='truncate font-medium'>
                      {t(I18N_PREFIX + 'requirement_profile')}
                    </span>
                  </div>
                </div>
                <div className='ml-4 flex-shrink-0'>
                  <span className='font-medium text-blue-600 group-hover:text-blue-500'>
                    {t(I18N_PREFIX + 'requirement_profile_show_action')}
                  </span>
                </div>
              </li>
              {careerAnalysesStatus === 'success' &&
                careerAnalyses &&
                careerAnalyses.length > 0 && (
                  <li className='mt-6 bg-gray-50 px-4 py-4'>
                    <div className='flex items-center'>
                      <svg
                        className='h-4 w-4 flex-shrink-0 text-gray-500'
                        fill='none'
                        viewBox='0 0 24 24'
                        strokeWidth={1.5}
                        stroke='currentColor'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          d='M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z'
                        />
                      </svg>
                      <span className='ml-2 text-sm font-medium text-gray-700'>
                        {t('career.overview.expert_analyses_section_title')}
                      </span>
                    </div>
                  </li>
                )}
              {careerAnalysesStatus === 'success' &&
                careerAnalyses &&
                careerAnalyses.length > 0 &&
                careerAnalyses
                  .filter((analyse) => {
                    const isPreset = !!analyse.preset
                    const isCompleted = analyse.status === 'COMPLETED'
                    const isCopy = !!analyse.copy_from_careeranalyse_id
                    // Nur fertige Analysen anzeigen: Presets, Kopien oder abgeschlossene
                    return isPreset || isCopy || isCompleted
                  })
                  .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
                  .map((analyse, index) => {
                    const isPreset = !!analyse.preset
                    const isCopy = !!analyse.copy_from_careeranalyse_id

                    // Robuste Behandlung der Anzeige-Werte
                    let displayValue = 'Unbekannt'

                    if (isPreset && analyse.preset?.name) {
                      displayValue =
                        analyse.preset.name[language] ||
                        analyse.preset.name.de ||
                        analyse.preset.name.en ||
                        'Preset'
                    } else if (analyse.person) {
                      // Priorisierung: email > invitation_email > firstname lastname > person_id
                      const email =
                        analyse.person.email || analyse.person.invitation_email
                      const name = [
                        analyse.person.firstname ||
                          analyse.person.invitation_firstname,
                        analyse.person.lastname ||
                          analyse.person.invitation_lastname
                      ]
                        .filter(Boolean)
                        .join(' ')

                      displayValue =
                        email || name || `Person (ID: ${analyse.person_id})`
                    } else if (analyse.person_id) {
                      displayValue = `Person (ID: ${analyse.person_id})`
                    }

                    const statusText = isPreset
                      ? 'Preset'
                      : isCopy
                        ? 'Kopiert'
                        : 'Experte'

                    const statusColor =
                      isPreset || isCopy ? 'text-indigo-600' : 'text-blue-600'

                    return (
                      <li
                        key={analyse.id || index}
                        className='flex items-center justify-between bg-white py-3 pl-8 pr-4 text-sm leading-6'
                      >
                        <div className='flex w-0 flex-1 items-center'>
                          <div className='flex min-w-0 flex-1 gap-2'>
                            <span className='truncate text-gray-900'>
                              {displayValue}
                            </span>
                            {!analyse.person && !isPreset && (
                              <span className='text-xs italic text-gray-500'>
                                (Person nicht mehr verfügbar)
                              </span>
                            )}
                          </div>
                        </div>
                        <div className='ml-4 flex-shrink-0'>
                          <span
                            className={`text-xs font-medium ${statusColor}`}
                          >
                            {statusText}
                          </span>
                        </div>
                      </li>
                    )
                  })}
            </ul>
          </div>
        </Panel>
        {dvinciIntegration && (
          <Panel className='mt-4'>
            <div>
              <h3 className='text-lg font-medium leading-6 text-gray-900'>
                {t('career.overview.dvinci_section_title')}
              </h3>
              <p className='mt-1 max-w-lg text-sm text-gray-700 lg:max-w-2xl'>
                {t('career.overview.dvinci_section_description')}
              </p>
            </div>
            <div className='mt-4 max-w-2xl rounded-md bg-white px-4 py-3'>
              <div className='flex items-center'>
                <LinkIcon className='h-6 w-6 text-gray-700' />
                <code className='ml-3 mr-4 truncate text-xs text-gray-900'>
                  <a
                    className='text-blue-700 underline visited:text-blue-800 hover:text-blue-500'
                    href={dvinciUrl}
                    target='_blank'
                    rel='noreferrer'
                  >
                    {dvinciUrl}
                  </a>
                </code>
                <button
                  type='button'
                  title={t('global.copy_action')}
                  aria-label={t('global.copy_action')}
                  className='ml-auto flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full bg-primaryBlue hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                  onClick={copyDvinciUrlToClipboard}
                >
                  <AnimatedCopyIcon
                    copied={dvinciUrlCopy.copied}
                    icon={ClipboardDocumentListIcon}
                    className='size-5 text-white'
                    checkClassName='text-white'
                  />
                </button>
              </div>
            </div>
          </Panel>
        )}

        <Panel className='mt-4'>
          <div>
            <h3 className='text-lg font-medium leading-6 text-gray-900'>
              {t('career.overview.challenges_section_title')}
            </h3>
            <p className='mt-1 max-w-lg text-sm text-gray-700 lg:max-w-2xl'>
              {t('career.overview.challenges_section_description')}
            </p>
          </div>
          <div className='mt-8 max-w-4xl'>
            <div className='flex flex-wrap gap-4'>
              {challenges.map((challenge, index) => (
                <ChallengeMaterialCard
                  key={index}
                  challenge={challenge}
                  className='border border-gray-200 bg-white'
                />
              ))}
            </div>
          </div>
        </Panel>
        <Feedback context='CAREER' />
      </div>
      <ProfileFeedbackModal
        open={displayFeedbackModal}
        setOpen={() => setDisplayFeedbackModal(false)}
        context={REQUIREMENTPROFILE}
      />
    </>
  )
}

Overview.propTypes = {
  career: PropTypes.object.isRequired,
  className: PropTypes.string
}

export default Overview
